import { initializeApp } from "firebase/app";
import { connectAuthEmulator, getAuth } from "firebase/auth";
import {
    connectFirestoreEmulator,
    addDoc,
    arrayRemove,
    arrayUnion,
    collection,
    deleteField,
    doc,
    getDoc,
    getDocs,
    getFirestore,
    query,
    setDoc,
    updateDoc,
    where,
} from "firebase/firestore";
import {
    getFirebaseConfig,
    getServerUrl,
} from "../../../lib/runtime-config.js";

class DatabaseManager {
    constructor() {
        this.firebaseConfig = getFirebaseConfig();

        this.app = initializeApp(this.firebaseConfig);
        this.db = getFirestore(this.app);
        this.auth = getAuth(this.app);
        this.firestore = getFirestore(this.app);

        this.USE_EMULATOR =
            typeof window !== "undefined" &&
            window.localStorage?.getItem("useFirebaseEmulator") === "true";

        if (this.USE_EMULATOR) {
            connectFirestoreEmulator(this.db, "127.0.0.1", 8080);
            connectAuthEmulator(this.auth, "http://127.0.0.1:9099", {
                disableWarnings: true,
            });
            console.warn("Firebase pointing at the local emulator suite");
        }

        this.API_BASE = getServerUrl();
    }

    currentUser() {
        return db.auth.currentUser?.uid;
    }

    waitForAuthUser() {
        return new Promise((resolve) => {
            const unsubscribe = this.auth.onAuthStateChanged((user) => {
                unsubscribe();
                resolve(user);
            });
        });
    }

    async getAuthToken() {
        return await db.auth.currentUser.getIdToken();
    }

    async apiRequest(endpoint, options = {}) {
        const token = await this.getAuthToken();
        const response = await fetch(`${this.API_BASE}${endpoint}`, {
            ...options,
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
                ...options.headers,
            },
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || `HTTP ${response.status}`);
        }

        return response.json();
    }

    async isUserAdmin() {
        try {
            const result = await this.apiRequest(`/api/admin`);
            return result.isAdmin;
        } catch (error) {
            throw new Error(error);
        }
    }

    async getAdminUids() {
        try {
            const result = await this.apiRequest(`/api/admins`);
            return result.uids;
        } catch (error) {
            throw new Error(error);
        }
    }

    async createDemoRoom(name) {
        const response = await fetch(`${this.API_BASE}/api/rooms/demo`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ name }),
        });

        const result = await response.json();
        if (!response.ok) throw new Error(result.error || `HTTP ${response.status}`);

        return result;
    }

    async getTeacherAvatar() {
        try {
            const result = await this.apiRequest(`/api/avatar`);
            return result.avatar ?? null;
        } catch (error) {
            throw new Error(error);
        }
    }

    async updateTeacherAvatar(avatar) {
        try {
            return await this.apiRequest(`/api/avatar`, {
                method: "PUT",
                body: JSON.stringify({ avatar }),
            });
        } catch (error) {
            throw new Error(error);
        }
    }

    getTeacherRef(uid) {
        return doc(this.firestore, "teachers", uid);
    }

    getRoomRef(roomId) {
        return doc(this.firestore, "rooms", roomId);
    }

    async getRoomById(roomUid) {
        try {
            const roomRef = this.getRoomRef(roomUid);
            const roomSnap = await getDoc(roomRef);

            return roomSnap.data();
        } catch (error) {
            throw new Error(error);
        }
    }

    async getRoom(roomName) {
        try {
            const roomsRef = collection(this.db, "rooms");
            const q = query(roomsRef, where("name", "==", roomName));
            const snapshot = await getDocs(q);

            if (snapshot.empty) return null;

            const doc = snapshot.docs[0];
            return {
                id: doc.id,
                ...doc.data(),
            };
        } catch (error) {
            throw new Error(error);
        }
    }

    async getTeacherData(uid) {
        try {
            const teacherDocRef = this.getTeacherRef(uid);
            const teacherDocSnap = await getDoc(teacherDocRef);

            return teacherDocSnap.data();
        } catch (error) {
            throw new Error(error);
        }
    }

    async getTeachers() {
        try {
            const teachersRef = collection(this.firestore, "teachers");
            const snapshot = await getDocs(teachersRef);

            if (snapshot.empty) return [];

            return snapshot.docs.map((doc) => ({
                uid: doc.id,
                ...doc.data(),
            }));
        } catch (error) {
            throw new Error(error);
        }
    }

    async registerTeacher({ email, password, name, surname }) {
        try {
            const result = await this.apiRequest("/api/teachers", {
                method: "POST",
                body: JSON.stringify({ email, password, name, surname }),
            });

            return result;
        } catch (error) {
            throw new Error(error);
        }
    }

    async deleteTeacher(email) {
        const teacher = await this.getTeacherByEmail(email);
        const token = await this.getAuthToken();

        const res = await this.apiRequest(`/api/teachers/${teacher.id}`, {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.success) throw new Error(await res.error);
        return res;
    }

    async getTeacherByEmail(email) {
        try {
            const ref = collection(this.firestore, "teachers");
            const q = query(ref, where("email", "==", email));
            const snapshot = await getDocs(q);

            if (snapshot.empty) return null;

            const doc = snapshot.docs[0];
            return {
                id: doc.id,
                ...doc.data(),
            };
        } catch (error) {
            throw new Error(error);
        }
    }

    async addRoom(roomId) {
        try {
            const roomsRef = collection(this.firestore, "rooms");
            const newRoomRef = doc(roomsRef);
            const data = {
                name: roomId,
                allowedStudents: [],
                teacher: this.currentUser(),
                isPrivate: true,
            };

            await setDoc(newRoomRef, data);
            return newRoomRef.id;
        } catch (error) {
            throw new Error(error);
        }
    }

    async roomAlreadyExits(roomName) {
        try {
            const roomsRef = collection(this.firestore, "rooms");
            const q = query(roomsRef, where("name", "==", roomName));
            const snapshot = await getDocs(q);

            return !snapshot.empty;
        } catch (error) {
            throw new Error(error);
        }
    }

    async updateRoomPrivacy(roomId, isPrivate) {
        try {
            const roomRef = this.getRoomRef(roomId);
            await updateDoc(roomRef, { isPrivate: isPrivate });
        } catch (error) {
            throw new Error(error);
        }
    }

    async updateRoomLockState(roomId, locked) {
        try {
            const roomRef = this.getRoomRef(roomId);
            await updateDoc(roomRef, { locked: locked });
        } catch (error) {
            throw new Error(error);
        }
    }

    async roomLocked(roomName) {
        try {
            const roomsRef = collection(this.firestore, "rooms");
            const q = query(roomsRef, where("name", "==", roomName));
            const snapshot = await getDocs(q);

            if (snapshot.empty) throw new Error("Room not found");

            const doc = snapshot.docs[0];
            return doc.data().locked;
        } catch (error) {
            throw new Error(error);
        }
    }

    async getTeacherRooms() {
        try {
            const roomRef = collection(this.db, "rooms");
            const q = query(
                roomRef,
                where("teacher", "==", this.currentUser()),
            );
            const snapshot = await getDocs(q);

            if (snapshot.empty) return [];
            return snapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
            }));
        } catch (error) {
            throw new Error(error);
        }
    }

    async addStudent(roomId, studentName) {
        try {
            const roomRef = this.getRoomRef(roomId);
            await updateDoc(roomRef, {
                allowedStudents: arrayUnion(studentName),
            });
        } catch (error) {
            throw new Error(error);
        }
    }

    async deleteStudent(roomId, studentName) {
        try {
            const roomRef = this.getRoomRef(roomId);
            await updateDoc(roomRef, {
                allowedStudents: arrayRemove(studentName),
            });
        } catch (error) {
            throw new Error(error);
        }
    }

    async deleteRoom(roomId) {
        return this.apiRequest(`/api/rooms/${roomId}`, { method: "DELETE" });
    }
}

const db = new DatabaseManager();
export default db;
